import { useState, useCallback, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
} from "src/components/Drawer";
import {
    TaskListFormDrawerProps,
    TaskListFormDrawerState,
    TaskListFormDrawerStateProps,
} from "./TaskListFormDrawer.types";
import { TaskListForm }  from "../TaskListForm";
import { TaskListModel } from "src/api";
import { UpdateTaskListModelHandler } from "../TaskListForm/TaskListForm.types";
import { UnstyledButton } from "src/ui";
import { SmallBodyBolder } from "src/components/Text";
import { PrimaryButton } from "@fluentui/react";
import { localize } from "src/l10n";
import { createTaskListAsync, updateTaskListAsync } from "src/api";
import { taskListCreated, taskListUpdated, findTaskList } from "src/redux";
import { SpintrTypes } from "src/typings";
import { Conditional } from "src/components/Conditional";
import SpintrLoader from "src/ui/components/Loader";
import { setConfirmPopup } from "src/popups/actions";

const initialState: TaskListFormDrawerState = {
    isDirty: false,
    isSaving: false,
} as const;

const initialModel: TaskListModel = {
    name: "",
    listType: SpintrTypes.TaskListType.CheckList,
    listTarget: SpintrTypes.TaskListTarget.Departments,
    recurrenceReferenceDate: new Date(),
    isRecurring: false,
    startDate: new Date(),
    contentStatus: SpintrTypes.ContentStatus.Published,
    allowOverride: true,
} as const;

/**
 * Renders a TaskListForm as a Drawer.
 * 
 * @param {TaskListFormDrawerProps} props - The properties to configure the TaskListFormDrawer.
 * @returns {JSX.Element | null} The rendered TaskListFormDrawer component.
 */
function TaskListFormDrawer({
    onDismiss,
    open,
    taskListId,
}: TaskListFormDrawerProps): JSX.Element | null {
    const {
        taskList,
        taskListLoading,
    } = useSelector<Spintr.AppState, TaskListFormDrawerStateProps>(
        (appState) => ({
            taskList: appState.qualityHub.listsById[taskListId!],
            taskListLoading: appState.qualityHub.listsLoading[taskListId!],
        }),
    );

    const [state, setState] = useState<TaskListFormDrawerState>(initialState);
    const [model, setModel] = useState<TaskListModel>(initialModel);
    
    const dispatch = useDispatch();

    const isNew = !taskListId;
    const { isSaving } = state;

    const l10n = useMemo(() => ({
        createNew: localize("QH_CREATE_NEW_LIST"),
        edit: localize("Redigera"),
        cancel: localize("Avbryt"),
        save: localize("Spara"),
    }), []);

    const onModelChanging = useCallback<UpdateTaskListModelHandler>(
        (updater) => {
            setModel((model) => updater(model));
            setState((state) => ({ ...state, isDirty: true }));
        },
        [setModel]);

    const onDismissing = useCallback(() => {
        if (!state.isDirty) {
            setModel(initialModel);
            onDismiss?.(false);
            return;
        }

        dispatch(setConfirmPopup({
            isOpen: true,
            message: localize("UnsavedChangesWarning"),
            onConfirm: () => {
                setModel(initialModel);
                setState((state) => ({ ...state, isDirty: false }));
                onDismiss?.(false);
            },
        }));
    }, [onDismiss, state.isDirty, setModel, setState, dispatch]);

    const onCancelClicked = useCallback(() => onDismissing(), [onDismissing]);

    const onSubmitting = useCallback(
        async function TaskListFormDrawerSubmitting() {
            if (isSaving) {
                return;
            }

            setState((state) => ({ ...state, isSaving: true }));

            let taskList: Spintr.TaskList | undefined;
            try {
                taskList = !taskListId
                    ? await createTaskListAsync(model)
                    : await updateTaskListAsync(taskListId, model);
            } catch (err) {
                console.error(err);

                setState((state) => ({ ...state, isSaving: false }));
                return;
            }

            if (!taskList) {
                return;
            }

            setState((state) => ({ ...state, isSaving: false }));
            dispatch(!taskListId
                ? taskListCreated(taskList)
                : taskListUpdated(taskList));
            setModel(initialModel);

            onDismiss?.(true, taskList);
        }, [isSaving, dispatch, taskListId, onDismiss, model]);

    const onOpenChange = useCallback((open?: boolean) => {
        if (open) {
            return;
        }

        onDismissing();
    }, [onDismissing]);

    useEffect(() => {
        if (!taskListId) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setModel(initialModel);
            return;
        }

        if (!taskList) {
            dispatch(findTaskList(taskListId));
            return;
        }

        setModel({ ...taskList });
    }, [taskListId, taskList, dispatch]);

    return (
        <Drawer className="TaskListFormDrawer"
                onOpenChange={onOpenChange}
                open={open}
                position="end"
                size="medium">
            <DrawerHeader>
                <DrawerHeaderTitle>
                    {isNew
                        ? l10n.createNew
                        : `${l10n.edit} ${taskList?.name ?? ""}`}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                <Conditional condition={taskListLoading || isSaving}>
                    <SpintrLoader />
                </Conditional>
                <Conditional condition={!taskListLoading && !isSaving}>
                    <TaskListForm model={model}
                                  onModelChanged={onModelChanging} />
                </Conditional>
            </DrawerBody>
            <DrawerFooter className="TaskListFormDrawer-footer">
                <UnstyledButton className="TaskListFormDrawer-cancel"
                                disabled={isSaving}
                                onClick={onCancelClicked}>
                    <SmallBodyBolder color="contentLight">
                        {l10n.cancel}
                    </SmallBodyBolder>
                </UnstyledButton>
                <div className="TaskListFormDrawer-submit">
                    <PrimaryButton disabled={isSaving}
                                   onClick={onSubmitting}
                                   type="submit">
                        {l10n.save}
                    </PrimaryButton>
                </div>
            </DrawerFooter>
        </Drawer>
    );
}

export default TaskListFormDrawer;

